import { injectable } from 'inversify';
import { assign } from 'lodash-es';
import * as yup from 'yup';
import {
    ALL_WORKFLOWACTION,
    ALL_WORKFLOWRECIPIENTS,
    ALL_WORKFLOWSTATE,
    ALL_WORKFLOWTRIGGER,
    WorkflowAction,
    WorkflowInput,
    WorkflowQuery,
    WorkflowRecipients,
    WorkflowState,
    WorkflowTrigger
} from '../generated/types';
import { TranslationService } from '../services/translationService';
import { isNonEmptyArray } from '../util/array';
import { InputService } from './inputService';

export interface ICreateUpdateWorkflowValues {
    workflow: WorkflowInput;
}

@injectable()
export class WorkflowInputService extends InputService {
    constructor(translationService: TranslationService) {
        super(translationService);
    }

    workflowInputDefault(workflow?: WorkflowQuery['event']['workflow']): WorkflowInput {
        return {
            name: workflow?.name ?? '',
            state: workflow?.state ?? WorkflowState.Active,
            trigger: workflow?.trigger ?? WorkflowTrigger.VolunteersSegment,
            action: workflow?.action ?? WorkflowAction.SendCampaign,
            data: assign({ recipients: WorkflowRecipients.View }, workflow?.data ?? {})
        };
    }

    workflowInputSchema() {
        return yup.object().shape({
            name: yup.string().required(this.t('le_nom_est_requ_61536')),
            state: yup
                .string()
                .required(this.t('le_statut_est_r_13164'))
                .oneOf(ALL_WORKFLOWSTATE, this.t('le_statut_est_r_13164')),
            action: yup
                .string()
                .required(this.t('l_action_est_re_08934'))
                .oneOf(ALL_WORKFLOWACTION, this.t('l_action_est_re_08934')),
            trigger: yup
                .string()
                .required(this.t('le_d_clencheur_32555'))
                .oneOf(ALL_WORKFLOWTRIGGER, this.t('le_d_clencheur_32555')),
            data: yup
                .mixed()
                .required()
                .test(
                    'positions-valid',
                    this.t('les_missions_so_67594'),
                    function (this: { parent: WorkflowInput }) {
                        if (this.parent.trigger === WorkflowTrigger.Assigned) {
                            return (
                                isNonEmptyArray(this.parent.data.positionsCategoriesIds) ||
                                isNonEmptyArray(this.parent.data.positionsIds) ||
                                isNonEmptyArray(this.parent.data.positionsSlotsIds) ||
                                isNonEmptyArray(this.parent.data.positionsSegmentsIds)
                            );
                        } else {
                            return true;
                        }
                    }
                )
                .test(
                    'volunteers-segment-id-valid',
                    this.t('la_vue_est_requ_65480'),
                    function (this: { parent: WorkflowInput }) {
                        if (this.parent.trigger === WorkflowTrigger.VolunteersSegment) {
                            return typeof this.parent.data.volunteersSegmentId === 'number';
                        } else {
                            return true;
                        }
                    }
                )
                .test(
                    'campaign-id-valid',
                    this.t('la_campagne_est_06708'),
                    function (this: { parent: WorkflowInput }) {
                        if (
                            this.parent.trigger === WorkflowTrigger.VolunteersSegment &&
                            this.parent.action === WorkflowAction.SendCampaign
                        ) {
                            return typeof this.parent.data.campaignId === 'number';
                        } else {
                            return true;
                        }
                    }
                )
                .test(
                    'campaign-recipients-valid',
                    this.t(this.t('les_destinatair_82420')),
                    function (this: { parent: WorkflowInput }) {
                        if (
                            this.parent.trigger === WorkflowTrigger.VolunteersSegment &&
                            this.parent.action === WorkflowAction.SendCampaign
                        ) {
                            return ALL_WORKFLOWRECIPIENTS.includes(this.parent.data.recipients);
                        } else {
                            return true;
                        }
                    }
                )
                .test(
                    'campaign-recipients-users-infos',
                    this.t('la_liste_des_me_55886'),
                    function (this: { parent: WorkflowInput }) {
                        if (
                            this.parent.trigger === WorkflowTrigger.VolunteersSegment &&
                            this.parent.action === WorkflowAction.SendCampaign &&
                            this.parent.data.recipients === WorkflowRecipients.UsersInfosIds
                        ) {
                            return isNonEmptyArray(this.parent.data.usersInfosIds);
                        } else {
                            return true;
                        }
                    }
                )
                .test(
                    'custom-field-id-valid',
                    this.t('le_champ_person_80074'),
                    function (this: { parent: WorkflowInput }) {
                        if (
                            this.parent.trigger === WorkflowTrigger.VolunteersSegment &&
                            this.parent.action === WorkflowAction.SetCustomField
                        ) {
                            return typeof this.parent.data.customFieldId === 'number';
                        } else {
                            return true;
                        }
                    }
                )
                .test(
                    'accreditations-valid',
                    this.t('les_accr_ditati_27943'),
                    function (this: { parent: WorkflowInput }) {
                        if (
                            this.parent.trigger === WorkflowTrigger.Assigned ||
                            (this.parent.trigger === WorkflowTrigger.VolunteersSegment &&
                                (this.parent.action === WorkflowAction.AddAccreditations ||
                                    this.parent.action === WorkflowAction.DeleteAccreditations))
                        ) {
                            return isNonEmptyArray(this.parent.data.accreditationsSlotsIds);
                        } else {
                            return true;
                        }
                    }
                )
        });
    }
}
